import { lazy, type ComponentType, type LazyExoticComponent, type ReactElement, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import RequireAuth from './RequireAuth';
import RequireRouteAccess from './RequireRouteAccess';
import MainLayout from '../layouts/MainLayout';
import LoadingState from '../components/LoadingState';
import ErrorBoundary from '../components/ErrorBoundary';
import { minimumRoleForRoute } from './routePermissions';

const AnalyticsLayout = lazy(() => import('../layouts/AnalyticsLayout'));
const AnalyticsIndex = lazy(() => import('../pages/analytics/AnalyticsIndex'));
const AnalyticsInductPage = lazy(() => import('../pages/analytics/AnalyticsInductPage'));
const AnalyticsPPHPage = lazy(() => import('../pages/analytics/AnalyticsPPHPage'));
const FloorPlan = lazy(() => import('../pages/FloorPlan'));
const Tasks = lazy(() => import('../pages/Tasks'));
const Disables = lazy(() => import('../pages/Disables'));
const Users = lazy(() => import('../pages/Users'));
const Settings = lazy(() => import('../pages/Settings'));
const Customers = lazy(() => import('../pages/Customers'));
const Packages = lazy(() => import('../pages/Packages'));
const ContainerTypes = lazy(() => import('../pages/ContainerTypes'));
const Containers = lazy(() => import('../pages/Containers'));

function lazyPage(label: string, Page: LazyExoticComponent<ComponentType<unknown>>) {
  return (
    <Suspense fallback={<LoadingState label={label} />}>
      <Page />
    </Suspense>
  );
}

export default function AppRoutes(): ReactElement {
  return (
    <BrowserRouter basename='/management-console'>
      <Routes>
        <Route
          path='/'
          element={
            <RequireAuth>
              <ErrorBoundary label='Layout'>
                <MainLayout />
              </ErrorBoundary>
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to='/analytics' replace />} />
          <Route
            path='floor-plan'
            element={
              <RequireRouteAccess minimumRole={minimumRoleForRoute('floor-plan')}>
                <ErrorBoundary label='Floor Plan'>{lazyPage('Loading floor plan…', FloorPlan)}</ErrorBoundary>
              </RequireRouteAccess>
            }
          />
          <Route
            path='tasks'
            element={
              <RequireRouteAccess minimumRole={minimumRoleForRoute('tasks')}>
                <ErrorBoundary label='Tasks'>{lazyPage('Loading tasks…', Tasks)}</ErrorBoundary>
              </RequireRouteAccess>
            }
          />
          <Route
            path='disables'
            element={
              <RequireRouteAccess minimumRole={minimumRoleForRoute('disables')}>
                <ErrorBoundary label='Disables'>{lazyPage('Loading disables…', Disables)}</ErrorBoundary>
              </RequireRouteAccess>
            }
          />
          <Route
            path='customers'
            element={
              <RequireRouteAccess minimumRole={minimumRoleForRoute('customers')}>
                <ErrorBoundary label='Customers'>{lazyPage('Loading customers…', Customers)}</ErrorBoundary>
              </RequireRouteAccess>
            }
          />
          <Route
            path='users'
            element={
              <RequireRouteAccess minimumRole={minimumRoleForRoute('users')}>
                <ErrorBoundary label='Users'>{lazyPage('Loading users…', Users)}</ErrorBoundary>
              </RequireRouteAccess>
            }
          />
          <Route
            path='analytics'
            element={
              <RequireRouteAccess minimumRole={minimumRoleForRoute('analytics')}>
                <ErrorBoundary label='Analytics'>{lazyPage('Loading analytics…', AnalyticsLayout)}</ErrorBoundary>
              </RequireRouteAccess>
            }
          >
            <Route index element={lazyPage('Loading charts…', AnalyticsIndex)} />
            <Route path='induct' element={lazyPage('Loading induct chart…', AnalyticsInductPage)} />
            <Route path='pph' element={lazyPage('Loading PPH chart…', AnalyticsPPHPage)} />
          </Route>
          <Route
            path='settings'
            element={
              <RequireRouteAccess minimumRole={minimumRoleForRoute('settings')}>
                <ErrorBoundary label='Settings'>{lazyPage('Loading settings…', Settings)}</ErrorBoundary>
              </RequireRouteAccess>
            }
          />
          <Route
            path='packages'
            element={
              <RequireRouteAccess minimumRole={minimumRoleForRoute('packages')}>
                <ErrorBoundary label='Packages'>{lazyPage('Loading packages…', Packages)}</ErrorBoundary>
              </RequireRouteAccess>
            }
          />
          <Route
            path='container-types'
            element={
              <RequireRouteAccess minimumRole={minimumRoleForRoute('container-types')}>
                <ErrorBoundary label='Container Types'>
                  {lazyPage('Loading container types…', ContainerTypes)}
                </ErrorBoundary>
              </RequireRouteAccess>
            }
          />
          <Route
            path='containers'
            element={
              <RequireRouteAccess minimumRole={minimumRoleForRoute('containers')}>
                <ErrorBoundary label='Containers'>{lazyPage('Loading containers…', Containers)}</ErrorBoundary>
              </RequireRouteAccess>
            }
          />
        </Route>
        <Route path='*' element={<Navigate to='/' replace />} />
      </Routes>
    </BrowserRouter>
  );
}
